import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { RiderLayout } from "@/components/rider/RiderLayout";
import { MobileEventSelectorV2 } from "@/components/rider/MobileEventSelectorV2";
import { DesktopEventSelectorV2 } from "@/components/rider/desktop/DesktopEventSelectorV2";
import { LoadingEvents } from "@/components/rider/LoadingEvents";
import { VehicleSelector } from "@/components/rider/VehicleSelector";
import { CommunityEvent } from "@/types/rider";

// Vehicle options with pricing
const vehicleOptions = [
  {
    id: "sprinter",
    name: "Sprinter",
    capacity: 15,
    pricing: { "4": 1000, "8": 1600 },
    icon: "🚐"
  },
  {
    id: "party-bus-med",
    name: "Party Bus Med",
    capacity: 24,
    pricing: { "4": 1500, "8": 2200 },
    icon: "🚌"
  },
  {
    id: "party-bus-large", 
    name: "Party Bus Large",
    capacity: 30,
    pricing: { "4": 1700, "8": 2500 },
    icon: "🚌"
  },
  {
    id: "executive-coach",
    name: "Exec Coach Bus",
    capacity: 30,
    pricing: { "4": 1200, "8": 1800 },
    icon: "🚍"
  }
];

// Breakaway Festival Events
const breakawayEvents: CommunityEvent[] = [
  {
    id: "breakaway-1",
    name: "Breakaway Festival",
    location: "The Palladium, 261 Main St, Worcester, MA 01608",
    region: "Boston",
    image: "/Breakaway_Promo_1.png",
    date: "Friday 9/5",
    startTime: "4 Hour Reservation | One Way",
    price: 0 // Will be set based on vehicle selection
  },
  {
    id: "breakaway-2", 
    name: "Breakaway Festival",
    location: "The Palladium, 261 Main St, Worcester, MA 01608",
    region: "Boston",
    image: "/Breakaway_Promo_1.png",
    date: "Friday 9/5",
    startTime: "8 Hour Reservation | Round Trip",
    price: 0 // Will be set based on vehicle selection
  },
  {
    id: "breakaway-3",
    name: "Breakaway Festival", 
    location: "The Palladium, 261 Main St, Worcester, MA 01608",
    region: "Boston",
    image: "/Breakaway_Promo_2.png",
    date: "Saturday 9/6",
    startTime: "4 Hour Reservation | One Way",
    price: 0 // Will be set based on vehicle selection
  },
  {
    id: "breakaway-4",
    name: "Breakaway Festival",
    location: "The Palladium, 261 Main St, Worcester, MA 01608", 
    region: "Boston",
    image: "/Breakaway_Promo_2.png",
    date: "Saturday 9/6",
    startTime: "8 Hour Reservation | Round Trip",
    price: 0 // Will be set based on vehicle selection
  }
];

const RiderEventSelectBreakawayV2 = () => {
  const [events, setEvents] = useState<CommunityEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();
  const [isImmersive, setIsImmersive] = useState(false);

  useEffect(() => {
    // Simulate loading delay for consistency with main event select page
    const loadEvents = () => {
      setTimeout(() => {
        setEvents(breakawayEvents);
        setLoading(false);
      }, 800);
    };

    loadEvents();
  }, []);

  const handleEventSelection = (eventId: string, index: number) => {
    setSelectedEventId(eventId || null);
    setActiveIndex(index);
  };

  const handleResetSelection = () => {
    setSelectedEventId(null);
    setSelectedVehicleId(null);
    setActiveIndex(0);
    setIsImmersive(false);
  };

  const handleVehicleSelection = (vehicleId: string) => {
    setSelectedVehicleId(vehicleId);
  };

  const handleBackToEvents = () => {
    setSelectedEventId(null);
    setSelectedVehicleId(null);
    setIsImmersive(false);
  };

  const handleBackToVehicles = () => {
    setSelectedVehicleId(null);
  };

  const handleContinue = (selectedAddOns?: any[], totalPrice?: number) => {
    if (selectedEventId && selectedVehicleId) {
      const selectedEvent = events.find(event => event.id === selectedEventId);
      const selectedVehicle = vehicleOptions.find(vehicle => vehicle.id === selectedVehicleId);
      const isRoundTrip = selectedEvent?.startTime?.includes('8 Hour') ? 'true' : 'false';
      const venueAddress = "The Palladium, 261 Main St, Worcester, MA 01608";
      
      const params = new URLSearchParams({ 
        eventId: selectedEventId,
        capacity: selectedVehicle?.capacity.toString() || '15',
        isRoundTrip: isRoundTrip,
        destination: venueAddress,
        vehicleType: selectedVehicle?.name || ''
      });
      
      // For round trips, handle return trip information
      if (isRoundTrip === 'true') {
        // Check if after-party add-on is selected for return destination
        const afterPartyAddon = selectedAddOns?.find(addon => 
          addon.description.includes('Ship Wrek') || addon.description.includes('DJ Diesel')
        );
        
        if (afterPartyAddon) {
          if (afterPartyAddon.description.includes('Ship Wrek')) {
            params.append('returnDestination', 'The Grand Boston, Seaport Boulevard, Boston, MA, USA');
          } else if (afterPartyAddon.description.includes('DJ Diesel')) {
            params.append('returnDestination', 'Big Night Live, Causeway Street, Boston, MA, USA');
          }
          params.append('returnPickup', venueAddress);
        } else {
          params.append('returnDestination', '');
          params.append('returnPickup', venueAddress);
        }
      }
      
      if (totalPrice) {
        params.append('price', totalPrice.toString());
      }
      
      // Create notes with vehicle information
      let notes = `${selectedVehicle?.name} - Capacity ${selectedVehicle?.capacity}`;
      
      if (selectedAddOns && selectedAddOns.length > 0) {
        params.append('addOns', JSON.stringify(selectedAddOns));
        
        const addOnNotes = selectedAddOns
          .map(addon => `${addon.description} (+$${addon.price})`)
          .join('\n');
        notes += '\n' + addOnNotes;
      }
      
      params.append('notes', notes);
      
      navigate(`/rider-quote?${params.toString()}`);
    }
  };

  const handleManualEntry = () => {
    handleResetSelection();
  };

  const handlePrevCard = () => {
    if (activeIndex > 0) {
      setActiveIndex(prev => prev - 1);
      setSelectedEventId(events[activeIndex - 1].id);
    }
  };

  const handleNextCard = () => {
    if (activeIndex < events.length - 1) {
      setActiveIndex(prev => prev + 1);
      setSelectedEventId(events[activeIndex + 1].id);
    }
  };

  // Mobile-specific navigation handlers that only change activeIndex
  const handleMobilePrevCard = () => {
    if (activeIndex > 0) {
      setActiveIndex(prev => prev - 1);
    }
  };

  const handleMobileNextCard = () => {
    if (activeIndex < events.length - 1) {
      setActiveIndex(prev => prev + 1);
    }
  };

  const handleCardTap = (eventId: string, index: number) => {
    if (!isImmersive) {
      setIsImmersive(true);
    }
    handleEventSelection(eventId, index);
  };

  return (
    <RiderLayout>
      <div className="min-h-screen bg-white transition-colors duration-300">
        {loading ? (
          <LoadingEvents />
        ) : (
          <>
            <MobileEventSelectorV2
              events={events}
              selectedEventId={selectedEventId}
              selectedVehicleId={selectedVehicleId}
              vehicleOptions={vehicleOptions}
              activeIndex={activeIndex}
              onEventSelect={handleEventSelection}
              onVehicleSelect={handleVehicleSelection}
              onContinue={handleContinue}
              onManualEntry={handleManualEntry}
              onPrevCard={handleMobilePrevCard}
              onNextCard={handleMobileNextCard}
              onBackToVehicles={handleBackToVehicles}
            />

            <DesktopEventSelectorV2
              events={events}
              activeIndex={activeIndex}
              selectedEventId={selectedEventId}
              selectedVehicleId={selectedVehicleId}
              vehicleOptions={vehicleOptions}
              isImmersive={isImmersive}
              setIsImmersive={setIsImmersive}
              handleEventSelection={handleEventSelection}
              handlePrevCard={handlePrevCard}
              handleNextCard={handleNextCard}
              handleCardTap={handleCardTap}
              handleContinue={handleContinue}
              handleManualEntry={handleManualEntry}
              handleResetSelection={handleResetSelection}
              handleVehicleSelection={handleVehicleSelection}
              handleBackToVehicles={handleBackToVehicles}
            />
          </>
        )}
      </div>
    </RiderLayout>
  );
};

export default RiderEventSelectBreakawayV2;